import { createLink, type LinkComponent } from '@tanstack/react-router'
import { forwardRef, useEffect, useState, type AnchorHTMLAttributes, type MouseEvent } from 'react'
import { cn } from '@/lib/utils'
import { SWITCH_HAPTIC_STYLE, switchHapticWanted, tapFeedback } from '../core/haptics'

// THE link for anything that drives a route transition — the section nav (app-section-nav) and the report tab
// strip (report-tabs) both render these. One component so the tap behaviour of a transition is defined once:
// the haptic, and (via the engine's onBeforeLoad hook) the slide that starts on this very frame.
//
// It stays a real `<a>` through TanStack's `createLink`, exactly like SegmentedTab: middle-clickable,
// shareable, SSR/reload-stable, and it still injects `aria-current="page"` on match. Styling is entirely the
// caller's — the two navs look nothing alike, and this component deliberately has no opinion about that.
//
// The iOS haptic overlay is RENDERED, from a flag an effect sets after mount — so it is absent from the SSR
// markup (it is platform-conditional and client-only) and appears as an ordinary post-mount update. It was
// briefly appended with `host.appendChild` instead; that put a node React has no fiber for inside a
// React-managed <a>, and a tap on it made React throw during the concurrent render and recover by
// SYNCHRONOUSLY re-rendering the whole root (recoverable error #520 — reported, and on prod deduped into an
// auto-filed GitHub issue) on every single nav on iOS. Rendering it keeps React's event system able to resolve
// the tap target to a fiber, which is all that was wrong. Guarded by route-transition-haptics.e2e.test.ts.

const TransitionLinkBase = forwardRef<HTMLAnchorElement, AnchorHTMLAttributes<HTMLAnchorElement>>(
  ({ onClick, className, children, ...props }, forwardedRef) => {
    // Resolved in an effect, not at render: it reads media queries + the Vibration API, so on the server (and
    // on the hydrating first client render) it must be false or the markup wouldn't match.
    const [wantsSwitch, setWantsSwitch] = useState(false)
    useEffect(() => setWantsSwitch(switchHapticWanted()), [])
    return (
      <a
        ref={forwardedRef}
        // `relative` unconditionally, so the overlay's `inset: 0` resolves against the link on the platform that
        // grows one, and the class list is identical on the server and the client either way. Harmless where
        // there is no overlay (the nav links are plain flex items), and a caller's own positioning still wins —
        // `cn` is tailwind-merge, so the later class does.
        className={cn('relative', className)}
        // Fire before delegating: the router's own handler runs inside `onClick` and kicks off the navigation
        // (and with it the glide), so the tick lands with the start of the motion rather than after it.
        //
        // Not for a tap that goes nowhere, though. The tick is the physical half of the SLIDE, and a link
        // pointing at the pathname we're already on doesn't slide — the pager stays in its slot (a search-only
        // change is still the same member), so a buzz there would be feedback for nothing happening. Compared
        // off the anchor's own resolved href rather than `aria-current`, whose default `includeSearch` misses
        // exactly that same-path/different-search case.
        onClick={(event: MouseEvent<HTMLAnchorElement>) => {
          if (event.currentTarget.pathname !== window.location.pathname) tapFeedback()
          onClick?.(event)
        }}
        {...props}
      >
        {children}
        {wantsSwitch ? (
          <input
            type="checkbox"
            // The `switch` ATTRIBUTE is the whole feature — a checkbox carrying it renders and behaves as a
            // system switch in Safari, which is what has a haptic. It isn't in React's prop types (nor in TS's
            // input attributes), and React 19 passes unknown lowercase attributes straight through, so it is set
            // on the node itself.
            ref={(el) => el?.setAttribute('switch', '')}
            // Invisible to the eye and to assistive tech; the <a> around it stays the accessible, focusable
            // control. It must NOT be a tab stop, and must NOT be advertised as a checkbox to anyone.
            tabIndex={-1}
            aria-hidden="true"
            style={SWITCH_HAPTIC_STYLE}
          />
        ) : null}
      </a>
    )
  },
)
TransitionLinkBase.displayName = 'TransitionLinkBase'

const CreatedTransitionLink = createLink(TransitionLinkBase)

export const TransitionLink: LinkComponent<typeof TransitionLinkBase> = (props) => <CreatedTransitionLink {...props} />
